import { motion } from "framer-motion";
import { Zap, TrendingUp, DollarSign, Cloud, Users, CheckCircle } from "lucide-react";

export default function AchievementsSection() {
    
    // Grouped and Enhanced Data Structure
    const achievementGroups = [
        {
            title: "Scaling & Growth",
            icon: TrendingUp,
            color: "indigo",
            stats: [
                { stat: '1M+', desc: 'Customers on Scaled Fintech Platforms' },
                { stat: '80+', desc: 'Engineers Led in a Distributed Org' },
                { stat: '40%', desc: 'Lower Latency via Microservices Arch.' }
            ]
        },
        {
            title: "Efficiency & Speed",
            icon: Zap,
            color: "sky",
            stats: [
                { stat: '75%', desc: 'Reduction in Release Cycles (4wks to 1wk)' },
                { stat: '30%', desc: 'Reduction in Cloud Operational Costs' },
                { stat: '99.9%', desc: 'Uptime on Cloud-Native Solutions' }
            ]
        }
    ];

    const allStats = [
        { stat: '1M+', desc: 'Customers on Scaled Fintech Platforms', icon: Users, color: 'text-indigo-400' },
        { stat: '80+', desc: 'Engineers Led in a Distributed Org', icon: TrendingUp, color: 'text-sky-400' },
        { stat: '75%', desc: 'Reduction in Release Cycles (4wks to 1wk)', icon: Zap, color: 'text-emerald-400' },
        { stat: '30%', desc: 'Reduction in Cloud Operational Costs', icon: DollarSign, color: 'text-pink-400' },
        { stat: '99.9%', desc: 'Uptime on Cloud-Native Solutions', icon: CheckCircle, color: 'text-yellow-400' },
        { stat: '40%', desc: 'Lower Latency via Microservices Arch.', icon: Cloud, color: 'text-cyan-400' }
    ];

    return (
        <section id="achievements" className="py-24 bg-slate-900 relative overflow-hidden">
            
            {/* Background Glow */}
            <div className="absolute inset-0 w-full h-full pointer-events-none opacity-20">
                <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-[100px]" />
                <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-sky-500/10 rounded-full blur-[80px]" />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <span className="text-sm font-semibold uppercase text-indigo-400 tracking-wider">Key Achievements</span>
                    <h2 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
                        Quantifiable Business <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-cyan-300">Impact</span>
                    </h2>
                    <p className="mt-4 text-lg text-gray-400">
                        These metrics demonstrate proven success in strategic platform engineering, driving efficiency, and achieving significant cost savings across multiple organizations.
                    </p>
                </div>
                
                {/* Statistics Grid - Uniform Professional Look */}
                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {allStats.map((item, index) => {
                        const Icon = item.icon;
                        return (
                            <motion.div
                                key={index}
                                className="p-1 rounded-xl bg-gradient-to-br from-slate-800/70 to-slate-900/70 shadow-xl shadow-black/30 border border-slate-700/50 hover:border-indigo-500/50 transition-all duration-300 group"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.4 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <div className="bg-slate-900 p-8 rounded-lg text-center h-full flex flex-col justify-center gap-3">
                                    <Icon className={`w-8 h-8 mx-auto mb-3 ${item.color} group-hover:scale-110 transition-transform`} />
                                    
                                    <span className="block text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-sky-300">
                                        {item.stat}
                                    </span>
                                    
                                    <span className="mt-2 block text-lg font-medium text-gray-300">
                                        {item.desc}
                                    </span>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    )
}